import { engineComponent, ArrayUtils, Logger } from "neo-common";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { StyleManager } from "../Components/Managers/StyleManager";
import { SwitchStyle, SwitchStyleCaseAttributes } from "../Components/Elements/Styles/SwitchStyle";
import { PlayList } from "../Components/Sounds/PlayList";
import { SongAttributes } from "../Components/Sounds/Song";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";

/**
 * SoundSwitchElement class is class which holds cases of sounds.
 * Can be defined only with SwitchStyle (See SwitchStyle class).
 * Default case is NONE.
 * Example 1:
 *  <MySoundSwitchElement Class="SoundSwitchElement" Style="SS_my_sounds"/>
 *
 *  <SS_my_sounds Class="SwitchStyle">
 *      <Cases>
 *          <Case Case="EMPTY"/>
 *          <Case Case="SOUND_1">
 *              <Song Song="SND_MyMagicSound" Play="true"/>
 *          </Case>
 *          <Case Case="SOUND_1_STOP">
 *              <Song Song="SND_MyMagicSound" Stop="true" FadeOut="30"/>
 *          </Case>
 *      </Cases>
 *  </SS_my_sounds>
 *
 * @class SoundSwitchElement
 * @author Marek Turkovic
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class SoundSwitchElement extends BaseElement {
    public name: string | null;
    // style is mandatory in this element
    protected style: SwitchStyle;
    protected case: string;
    protected timer: FrameTimerEventEmitter;

    constructor() {
        super();

        this.case = "NONE"; // default case
        this.timer = CreateFrameTimer("timer");
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element) {
        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute(SoundSwitchElementAttributes.STYLE)) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(SoundSwitchElementAttributes.STYLE)];
            if (this.style != null) {
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(SoundSwitchElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(SoundSwitchElementAttributes.CASE)) {
            this.goToCase(node.getAttribute(SoundSwitchElementAttributes.CASE));
        } else if (node.hasAttribute(SoundSwitchElementAttributes.RANDOM_CASE)) {
            this.goToCase(
                SoundSwitchElement.getRandomCase(node.getAttribute(SoundSwitchElementAttributes.RANDOM_CASE)),
            );
        }
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param {Element} node
     * @return void
     */
    public updateByNode(node: Element) {
        if (node.hasAttribute(SoundSwitchElementAttributes.CASE)) {
            this.goToCase(node.getAttribute(SoundSwitchElementAttributes.CASE));
        } else if (node.hasAttribute(SoundSwitchElementAttributes.RANDOM_CASE)) {
            this.goToCase(
                SoundSwitchElement.getRandomCase(node.getAttribute(SoundSwitchElementAttributes.RANDOM_CASE)),
            );
        }
    }

    /**
     * Find out if this element has case s.
     * @method hasCase
     * @param s name of case
     * @return Boolean
     */
    public hasCase(s: string): Boolean {
        return this.style != null && this.style.cases != null && this.style.cases[s];
    }

    /**
     * Returns xml element of case s.
     * @method getCase
     * @param s name of case
     * @return Element
     */
    public getCase(s: string): Element {
        if (this.hasCase(s)) {
            return this.style.cases[s];
        }
        return null;
    }

    /**
     * Get current set case.
     * @returns {string}
     */
    public getCurrentCase(): string {
        return this.case;
    }

    /**
     * Switch this element to case s.
     * @method goToCase
     * @param s name of case
     * @return void
     */
    public goToCase(s: string): void {
        if (this.style != null && this.style.cases != null && this.case !== s) {
            if (this.timer != null) {
                this.timer.removeAllListeners(FrameTimerEvents.Complete);
                this.timer.stop();
            }

            if (this.hasCase(s)) {
                let node: Element = this.style.cases[s];
                if (node != null) {
                    if (DEBUG) {
                        if (
                            node.hasAttribute(SoundSwitchElementAttributes.DEBUG) &&
                            node.getAttribute(SoundSwitchElementAttributes.DEBUG) === "true"
                        ) {
                            debugger;
                        }
                    }

                    this.case = s;

                    let next: Element = node.firstElementChild;
                    while (next != null) {
                        if (next.hasAttribute(SongAttributes.SONG)) {
                            if (next.hasAttribute(SongAttributes.PLAY)) {
                                if (next.hasAttribute(SongAttributes.FADEIN)) {
                                    PlayList.play(
                                        next.getAttribute(SongAttributes.SONG),
                                        parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                    );
                                } else {
                                    PlayList.play(next.getAttribute(SongAttributes.SONG));
                                }
                            }
                            if (next.hasAttribute(SongAttributes.LOOP)) {
                                if (next.hasAttribute(SongAttributes.FADEIN)) {
                                    PlayList.loop(
                                        next.getAttribute(SongAttributes.SONG),
                                        parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                    );
                                } else {
                                    PlayList.loop(next.getAttribute(SongAttributes.SONG));
                                }
                            }
                            if (next.hasAttribute(SongAttributes.STOP)) {
                                if (next.hasAttribute(SongAttributes.FADEOUT)) {
                                    PlayList.stop(
                                        next.getAttribute(SongAttributes.SONG),
                                        parseInt(next.getAttribute(SongAttributes.FADEOUT)),
                                    );
                                } else {
                                    PlayList.stop(next.getAttribute(SongAttributes.SONG));
                                }
                            }
                        }
                        next = next.nextElementSibling;
                    }

                    let nextCase: string = null;
                    if (node.hasAttribute(SoundSwitchStyleCaseAttributes.NEXT_CASE)) {
                        nextCase = node.getAttribute(SoundSwitchStyleCaseAttributes.NEXT_CASE);
                    } else if (node.hasAttribute(SwitchStyleCaseAttributes.NEXT_RANDOM_CASE)) {
                        nextCase = SoundSwitchElement.getRandomCase(
                            node.getAttribute(SwitchStyleCaseAttributes.NEXT_RANDOM_CASE),
                        );
                    }

                    if (nextCase != null && this.hasCase(nextCase)) {
                        if (node.hasAttribute(SoundSwitchStyleCaseAttributes.TIMER)) {
                            this.timer.on(FrameTimerEvents.Complete, this.goToCase.bind(this, nextCase));
                            this.timer.start(parseInt(node.getAttribute(SoundSwitchStyleCaseAttributes.TIMER)));
                        } else {
                            this.goToCase(nextCase);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no case: " + s + " for " + this.name);
                }
            }
        }
    }

    /**
     * Get random case from array of cases.
     * @param {string} s
     * @returns {string}
     */
    private static getRandomCase(s: string): string {
        let result = 0;

        let dataArray: Array<string> = ArrayUtils.stringArray(s);
        if (dataArray.length > 0) {
            let max = dataArray.length - 1;
            let min = 0;

            result = Math.round(SoundSwitchElement.getRandom(min, max));
        } else {
            return null;
        }

        return dataArray[result];
    }

    /**
     *
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    private static getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return SoundSwitchElement
     */
    clone(): SoundSwitchElement {
        const element: SoundSwitchElement = new SoundSwitchElement();

        element.name = this.name;
        element.style = this.style;
        element.case = this.case;
        element.markAsCloned(); // must be called last before return!

        return element;
    }
}

/**
 * XML has attributes SoundSwitchElementAttributes, BaseElementAttributes
 * @type {{STYLE: string; CASE: string}}
 */
export const SoundSwitchElementAttributes = {
    /**
     * Property case is used for set element to some case
     * @property Case
     * @type string
     * @default null
     */
    CASE: "Case",
    CLONE: "Clone",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",
    /**
     * Style holds elements as ContainerElement and also holds cases as xml nodes.
     * Style for switch element has to be instance of SwitchStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    /**
     * Multiplies timer with random number between min and max
     * @property Random
     * @type string
     * @default 1
     */
    RANDOM: "Random",
    /**
     * Property to select random case from array defined as: "Case1;Case2;..."
     * @property RandomCase
     * @type string
     */
    RANDOM_CASE: "RandomCase",
};

/**
 *
 */
export const SoundSwitchStyleCaseAttributes = {
    NEXT_CASE: "NextCase",
    TIMER: "Timer",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/SoundSwitchElement.ts
 **/